import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { Badge, Button, Card } from '@/components/ui';
import { getCasesNeedingMe } from '@/api/services/hrCases.service';
import { CASE_STATUS } from './caseFormat';

/**
 * Dashboard card for case team members: cases waiting on their next step.
 * Only the reference and status are shown, never the employee or type, and
 * it renders nothing when nothing is waiting or the count cannot be loaded,
 * so an empty card never hints at cases.
 */
export function CasesNeedingYouCard(): React.ReactElement | null {
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ['hr-cases-needing-me'],
    queryFn: getCasesNeedingMe,
    retry: false,
  });
  const cases = Array.isArray(data?.cases) ? data.cases : [];
  const total = typeof data?.total === 'number' ? data.total : 0;
  if (total === 0 || cases.length === 0) return null;

  return (
    <Card
      header={
        <div className="flex items-center gap-2">
          <Lock className="h-4 w-4 text-primary" aria-hidden />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            HR cases needing you
          </h2>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {total} {total === 1 ? 'case' : 'cases'}
          </span>
        </div>
      }
      contentClassName="p-0"
    >
      <ul className="divide-y divide-gray-200 dark:divide-gray-700">
        {cases.map((item) => {
          const status = CASE_STATUS[item.status];
          return (
            <li
              key={item._id}
              className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {item.kind === 'discipline' ? 'Disciplinary case' : 'Grievance'}
                </p>
                <p className="font-mono text-xs text-gray-600 dark:text-gray-300">
                  {item.reference}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={status?.variant ?? 'neutral'} size="sm">
                  {status?.label ?? item.status}
                </Badge>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => navigate(`/hr?company=${item.companyId}&tab=cases`)}
                >
                  Open <span className="sr-only">{item.reference}</span>
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
      {total > cases.length && (
        <p className="px-4 py-2 text-xs text-gray-600 dark:text-gray-300">
          Showing the {cases.length} longest waiting.
        </p>
      )}
    </Card>
  );
}
